import { useAuth0 } from '@auth0/auth0-react'

import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'

import { FormSubmissionDTO } from '../models/FormSubmissionDTO.model'
import { AuthStateContext } from '../providers/AuthStateContext'
import { FormService } from '../services/FormService/FormService'
import { Spinner } from './Spinner'
import { StarRating } from './StarRating'

const reviewTypeToFormId = {
  agency: Number(process.env.REACT_APP_AGENCY_REVIEW_FORM_ID) || 35,
  hospital: Number(process.env.REACT_APP_HOSPITAL_REVIEW_FORM_ID) || 34,
}

const formIdToReviewType = (formId: number): 'hospital' | 'agency' | 'unknown' => {
  if (formId === reviewTypeToFormId.hospital) return 'hospital'
  if (formId === reviewTypeToFormId.agency) return 'agency'
  return 'unknown'
}

const getFilterButtonClass = (isActive: boolean): string =>
  `rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
    isActive ? 'bg-orange text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
  }`

export const MyReviews: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const [reviews, setReviews] = useState<FormSubmissionDTO[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'hospital' | 'agency'>('all')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [reviewIdToDelete, setReviewIdToDelete] = useState<number | null>(null)
  const [deleteSuccess, setDeleteSuccess] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const history = useHistory()
  const formService = new FormService()

  useEffect(() => {
    // Wait for auth to be ready and token to be available
    if (!authLoading && isAuthenticated) {
      // Small delay to ensure token is set in AuthStateContext
      const timer = setTimeout(() => {
        const token = AuthStateContext.getToken()
        if (token) {
          loadReviews()
        } else {
          // If token is still not available, wait a bit more and retry
          const retryTimer = setTimeout(() => {
            loadReviews()
          }, 500)
          return () => clearTimeout(retryTimer)
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAuthenticated])

  const loadReviews = async () => {
    try {
      setIsLoading(true)
      setError('')

      // Check if token is available before making the request
      const token = AuthStateContext.getToken()
      if (!token) {
        // Wait a bit more for token to be available
        await new Promise((resolve) => setTimeout(resolve, 500))
        const retryToken = AuthStateContext.getToken()
        if (!retryToken) {
          setError('Authentication token not available. Please refresh the page.')
          setIsLoading(false)
          return
        }
      }

      const userReviews = await formService.getMyReviews()
      // Sort by most recent first
      userReviews.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setReviews(userReviews)
    } catch (err: unknown) {
      console.error('Error loading reviews:', err)
      // Handle 401 specifically
      const error = err as { status?: number; response?: { status?: number } }
      if (error?.status === 401 || error?.response?.status === 401) {
        setError('Your session has expired. Please refresh the page or log in again.')
      } else {
        setError('Failed to load your reviews. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteClick = (reviewId: number) => {
    setReviewIdToDelete(reviewId)
    setShowDeleteModal(true)
    setDeleteError('')
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setReviewIdToDelete(null)
    setDeleteError('')
  }

  const handleConfirmDelete = async () => {
    if (reviewIdToDelete === null) return

    try {
      setDeletingId(reviewIdToDelete)
      setDeleteError('')
      await formService.deleteSubmission(reviewIdToDelete)
      // Remove from list
      setReviews(reviews.filter((r) => r.id !== reviewIdToDelete))
      setShowDeleteModal(false)
      setDeleteSuccess(true)
      setTimeout(() => {
        setDeleteSuccess(false)
      }, 3000)
    } catch (err) {
      console.error('Error deleting review:', err)
      setDeleteError('Failed to delete review. Please try again.')
    } finally {
      setDeletingId(null)
      setReviewIdToDelete(null)
    }
  }

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showDeleteModal) {
      document.body.style.overflow = 'hidden'
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'hidden'
      }
    } else {
      document.body.style.overflow = ''
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'auto'
      }
    }
    return () => {
      document.body.style.overflow = ''
      const pageRoot = document.getElementById('page-root')
      if (pageRoot) {
        pageRoot.style.overflow = 'auto'
      }
    }
  }, [showDeleteModal])

  const handleEdit = (reviewId: number) => {
    history.push(`/edit-review/${reviewId}`)
  }

  const getRatingValue = (review: FormSubmissionDTO): number | null => {
    if (!review.answers) return null
    // Find the first rating field (isRating = true)
    const ratingAnswer = review.answers.find((answer) => answer.field?.isRating)
    if (ratingAnswer) {
      const rating = Number(ratingAnswer.value)
      return isNaN(rating) ? null : rating
    }
    return null
  }

  const getReviewSummary = (review: FormSubmissionDTO): string => {
    if (!review.answers || review.answers.length === 0) return 'No answers'

    // Try to find a textarea/feedback field
    const feedbackAnswer = review.answers.find(
      (answer) => answer.field?.type === 'textarea' && answer.value && answer.value.trim().length > 0,
    )
    if (feedbackAnswer && feedbackAnswer.value) {
      const text = feedbackAnswer.value.trim()
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    }

    // If no feedback, show first non-rating answer
    const firstAnswer = review.answers.find((answer) => !answer.field?.isRating && answer.value)
    if (firstAnswer && firstAnswer.value) {
      const text = firstAnswer.value.trim()
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    }

    return 'Review submitted'
  }

  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format('MMMM D, YYYY')
  }

  // Filter reviews based on search and type
  const filteredReviews = useMemo(() => {
    return reviews.filter((review) => {
      // Filter by type
      if (filterType !== 'all') {
        const reviewType = review.form ? formIdToReviewType(review.form.id) : 'unknown'
        if (reviewType !== filterType) return false
      }

      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        const summary = getReviewSummary(review).toLowerCase()
        const date = dayjs(review.createdAt).format('MMMM D, YYYY').toLowerCase()
        const reviewType = review.form ? formIdToReviewType(review.form.id) : 'unknown'
        const hospitalName = review.hospitalName?.toLowerCase() || ''
        const agencyName = review.agencyName?.toLowerCase() || ''

        if (
          !summary.includes(query) &&
          !date.includes(query) &&
          !reviewType.includes(query) &&
          !hospitalName.includes(query) &&
          !agencyName.includes(query)
        ) {
          return false
        }
      }

      return true
    })
  }, [reviews, searchQuery, filterType])

  const getReviewTypeColor = (reviewType: 'hospital' | 'agency' | 'unknown'): string => {
    switch (reviewType) {
      case 'hospital':
        return 'bg-aqua-mint text-white'
      case 'agency':
        return 'bg-orange text-white'
      default:
        return 'bg-gray-500 text-white'
    }
  }

  const getReviewBorderColor = (reviewType: 'hospital' | 'agency' | 'unknown'): string => {
    switch (reviewType) {
      case 'hospital':
        return 'border-aqua-mint'
      case 'agency':
        return 'border-orange'
      default:
        return 'border-gray-300'
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-[200px]">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
        {error}
        <button className="mt-2 text-sm underline hover:no-underline" onClick={loadReviews}>
          Try again
        </button>
      </div>
    )
  }

  if (reviews.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 text-center">
        <div className="flex justify-center mb-4">
          <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </div>
        </div>
        <h3 className="text-xl font-semibold text-graphite-grey mb-2">No reviews yet</h3>
        <p className="text-gray-500 mb-6">
          You haven&apos;t submitted any reviews yet. Share your experience to help other healthcare workers find the
          right fit.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/hospitals"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-aqua-mint text-white font-semibold rounded-lg hover:bg-opacity-90 transition-all shadow-md"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            Find a Hospital to Review
          </Link>
          <Link
            to="/hospitals#tab=agencies"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-orange text-white font-semibold rounded-lg hover:bg-opacity-90 transition-all shadow-md"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Find an Agency to Review
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Search and Filter Section */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search reviews..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 pl-10 focus:outline-none"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilterType('all')}
            className={getFilterButtonClass(filterType === 'all')}
          >
            All Reviews
          </button>
          <button
            type="button"
            onClick={() => setFilterType('hospital')}
            className={getFilterButtonClass(filterType === 'hospital')}
          >
            Hospitals
          </button>
          <button
            type="button"
            onClick={() => setFilterType('agency')}
            className={getFilterButtonClass(filterType === 'agency')}
          >
            Agencies
          </button>
        </div>

        {/* Results Count */}
        {filteredReviews.length !== reviews.length && (
          <div className="text-sm text-gray-600">
            Showing {filteredReviews.length} of {reviews.length} review{reviews.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Reviews List */}
      {filteredReviews.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 text-center">
          <p className="text-gray-600">No reviews found matching your search criteria.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReviews.map((review) => {
            const reviewType = review.form ? formIdToReviewType(review.form.id) : 'unknown'
            const rating = getRatingValue(review)
            const summary = getReviewSummary(review)
            const isDeleting = deletingId === review.id

            return (
              <div
                key={review.id}
                className={`rounded-lg border-2 bg-white p-4 shadow-md transition-all hover:shadow-lg sm:p-5 ${getReviewBorderColor(reviewType)}`}
              >
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      {(review.hospitalName || review.agencyName) && (
                        <span className="text-base font-semibold text-graphite-grey">
                          {review.hospitalName || review.agencyName}
                        </span>
                      )}
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold capitalize text-white ${getReviewTypeColor(reviewType)}`}
                      >
                        {reviewType} Review
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-500">{formatDate(review.createdAt)}</p>
                  </div>

                  {rating !== null && (
                    <div className="flex shrink-0 items-center gap-2">
                      <StarRating rating={rating} size="md" />
                      <span className="text-lg font-semibold text-graphite-grey">{rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>

                <p className="mb-3 text-base leading-relaxed text-gray-700">{summary}</p>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  {review.answers && review.answers.length > 0 ? (
                    <p className="text-sm text-gray-500">
                      {review.answers.length} {review.answers.length === 1 ? 'answer' : 'answers'}
                    </p>
                  ) : (
                    <span />
                  )}

                  <div className="ml-auto flex shrink-0 gap-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border-2 border-aqua-mint/50 bg-white px-5 py-2.5 text-sm font-semibold text-aqua-mint shadow-sm transition-colors hover:border-aqua-mint hover:bg-aqua-mint/8 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isDeleting}
                      onClick={() => handleEdit(review.id)}
                      title="Edit review"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span>Edit</span>
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-red-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={isDeleting}
                      onClick={() => handleDeleteClick(review.id)}
                      title="Delete review"
                    >
                      {isDeleting ? (
                        <>
                          <svg
                            className="h-4 w-4 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          <span>Delete</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={handleCancelDelete} />
          {/* Modal Content */}
          <div className="relative z-[10001] bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl border border-gray-100">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Delete Review</h2>
            <p className="text-base text-center text-gray-600 mb-8 leading-relaxed">
              Are you sure you want to delete this review? This action cannot be undone.
            </p>
            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                {deleteError}
              </div>
            )}
            <div className="flex gap-3 w-full">
              <button
                onClick={handleCancelDelete}
                disabled={deletingId !== null}
                className="flex-1 px-5 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingId !== null}
                className="flex-1 px-5 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md flex items-center justify-center gap-2"
              >
                {deletingId !== null ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {deleteSuccess && (
        <div className="fixed bottom-4 right-4 z-[10000] bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">Review deleted successfully</span>
        </div>
      )}
    </div>
  )
}
